"use client";

/**
 * Researching animation shared by the create-employer and enrich-employer
 * modals: dotted glow background, the logo (with color-sampled light beam
 * orbiting it), and a status line.
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { DottedGlowBackground } from "@/components/ui/dotted-glow-background";
import {
  LogoBeamBorder,
  useLogoPrimaryColor,
} from "@/components/ui/logo-beam";

export function EmployerResearchScreen({
  name,
  logoSrc,
  subtitle = "Building your employer profile",
}: {
  name: string;
  logoSrc: string | null;
  subtitle?: string;
}) {
  // Initials are a FALLBACK, not an overlay: show them only when there is no
  // logo source at all, or the logo image failed to load.
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = !!logoSrc && !logoFailed;
  const beamColor = useLogoPrimaryColor(showLogo ? logoSrc : null);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden rounded-xl">
      <DottedGlowBackground
        className="!absolute inset-0 h-full w-full"
        gap={10}
        radius={1}
        darkColor="rgba(255,255,255,0.45)"
        darkGlowColor="rgba(59, 130, 246, 0.65)"
        color="rgba(0,0,0,0.25)"
        glowColor="rgba(59, 130, 246, 0.55)"
        opacity={0.5}
        speedMin={0.5}
        speedMax={1.6}
        speedScale={1.1}
      />
      <div className="relative z-10 flex flex-col items-center gap-5">
        <div className="relative h-[76px] w-[76px]">
          <LogoBeamBorder color={beamColor} duration={3500} />
          <motion.div
            className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl border border-contrast/10 bg-surface-elevated shadow-sm"
            animate={{ scale: [1, 1.03, 1] }}
            transition={{
              duration: 2.4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {showLogo ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={logoSrc}
                alt={name}
                className="h-full w-full rounded-xl object-cover"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="text-2xl font-semibold text-contrast/30">
                {name.trim()[0]?.toUpperCase() || "?"}
              </span>
            )}
          </motion.div>
        </div>
        <div className="flex flex-col items-center gap-1.5 text-center">
          <p className="text-base font-semibold text-heading tracking-tight">
            Researching {name}
          </p>
          <p className="text-sm leading-5 text-heading/60">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}
